import React, { useContext, useEffect, useState } from "react";
import { compactPageItems } from "../../components/ui/compactPagination";
import { useNavigate } from "react-router-dom";
import { Table, Button, Form, InputGroup, Pagination, Badge, Spinner } from "react-bootstrap";
import api from "../../api";
import { AuthContext } from "../../context/AuthContext";
import TaskApprovalDecisionDialog from "../../components/tasks/TaskApprovalDecisionDialog";
import { toast } from "react-toastify";
import "./MyTaskApprovalQueuePage.css";

// Phase 4B, §20/§30. ONE common page for every role (no role-specific
// duplicate pages) — 3 tabs over the 2 my-approval endpoints
// (my-task-approval-queue with scope=pending/decided, my-task-approval-requests).
const TABS = [
  { key: "pending", label: "Pending My Decision" },
  { key: "my_requests", label: "My Approval Requests" },
  { key: "decided", label: "Completed Decisions" },
];

function requestStatusVariant(status) {
  switch (status) {
    case "PENDING": return "warning";
    case "APPROVED": return "success";
    case "REJECTED": return "danger";
    case "CHANGES_REQUESTED": return "info";
    case "CANCELLED": return "secondary";
    default: return "light";
  }
}

export default function MyTaskApprovalQueuePage() {
  const { user } = useContext(AuthContext) || { user: null };
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [status, setStatus] = useState("");
  const [taskTypeId, setTaskTypeId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [projectModuleId, setProjectModuleId] = useState("");
  const [priority, setPriority] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [taskTypes, setTaskTypes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const [decisionTarget, setDecisionTarget] = useState(null); // { requestId, stage, taskTitle }

  const fetchRows = async () => {
    try {
      setLoading(true);
      const params = {
        task_type_id: taskTypeId || undefined, project_id: projectId || undefined, project_module_id: projectModuleId || undefined,
        priority: priority || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined, search: search || undefined,
        page: currentPage, pageSize,
      };
      let res;
      if (tab === "my_requests") {
        res = await api.get("/my-task-approval-requests", { params: { ...params, status: status || undefined } });
      } else {
        res = await api.get("/my-task-approval-queue", { params: { ...params, scope: tab === "decided" ? "decided" : "pending" } });
      }
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotal(res.data?.meta?.total || 0);
    } catch (err) {
      console.error("Fetch approval queue error:", err);
      toast.error(err.response?.data?.message || "Failed to fetch approval data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, status, taskTypeId, projectId, projectModuleId, priority, dateFrom, dateTo, search, currentPage]);

  useEffect(() => {
    Promise.all([
      api.get("/task-types", { params: { is_active: 1, pageSize: 200 } }).catch(() => null),
      api.get("/projects", { params: { is_active: 1, pageSize: 200 } }).catch(() => null),
    ]).then(([ttRes, pRes]) => {
      setTaskTypes(Array.isArray(ttRes?.data?.data) ? ttRes.data.data : []);
      setProjects(Array.isArray(pRes?.data?.data) ? pRes.data.data : []);
    });
  }, []);

  const totalPages = Math.max(Math.ceil(total / pageSize), 1);
  const switchTab = (key) => { setTab(key); setCurrentPage(1); };

  const openDecide = (row) => {
    setDecisionTarget({ requestId: row.request_id, stage: { id: row.stage_id, stage_name: row.stage_name, stage_order: row.stage_order }, taskTitle: row.task_title });
  };

  return (
    <div className="unit-page taskapprovalqueue-theme">

      <div className="unit-header">
        <div>
          <div className="unit-page-label">Tasks</div>
          <h2>My Task Approvals</h2>
          <p>Approval decisions awaiting you, requests you've submitted, and decisions you've already made.</p>
        </div>
        <div className="unit-header-actions">
          <Button className="unit-btn unit-btn-light" onClick={fetchRows}>
            <i className="fa-solid fa-rotate" aria-hidden="true"></i>
            Refresh
          </Button>
        </div>
      </div>

      <div className="unit-card">
        <div className="unit-toolbar">
          <div className="unit-tabs">
            {TABS.map((t) => (
              <Button
                key={t.key}
                size="sm"
                variant={tab === t.key ? "primary" : "outline-secondary"}
                className={`unit-tab${tab === t.key ? " unit-tab-active" : ""}`}
                onClick={() => switchTab(t.key)}
              >
                {t.label}
              </Button>
            ))}
          </div>
          <div className="unit-toolbar-actions">
            <InputGroup className="unit-search">
              <InputGroup.Text aria-hidden="true">
                <i className="fa-solid fa-magnifying-glass"></i>
              </InputGroup.Text>
              <Form.Control
                placeholder="Search..."
                aria-label="Search approval queue"
                value={search}
                onChange={(e) => { setCurrentPage(1); setSearch(e.target.value); }}
              />
            </InputGroup>
            {tab === "my_requests" && (
              <Form.Select style={{ maxWidth: 170 }} value={status} onChange={(e) => { setCurrentPage(1); setStatus(e.target.value); }}>
                <option value="">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
                <option value="CHANGES_REQUESTED">Changes Requested</option>
                <option value="CANCELLED">Cancelled</option>
              </Form.Select>
            )}
            <Form.Select style={{ maxWidth: 150 }} value={priority} onChange={(e) => { setCurrentPage(1); setPriority(e.target.value); }}>
              <option value="">All Priorities</option>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </Form.Select>
            <Form.Select style={{ maxWidth: 170 }} value={taskTypeId} onChange={(e) => { setCurrentPage(1); setTaskTypeId(e.target.value); }}>
              <option value="">All Task Types</option>
              {taskTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Form.Select>
            <Form.Select style={{ maxWidth: 170 }} value={projectId} onChange={(e) => { setCurrentPage(1); setProjectId(e.target.value); setProjectModuleId(""); }}>
              <option value="">All Projects</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Form.Select>
            <Form.Control type="date" style={{ maxWidth: 150 }} value={dateFrom} onChange={(e) => { setCurrentPage(1); setDateFrom(e.target.value); }} />
            <Form.Control type="date" style={{ maxWidth: 150 }} value={dateTo} onChange={(e) => { setCurrentPage(1); setDateTo(e.target.value); }} />
          </div>
        </div>

        <div className="unit-table-wrapper">
          {loading ? (
            <div className="unit-loader"><Spinner animation="border" /><p>Loading...</p></div>
          ) : (
            <Table responsive className="unit-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Priority</th>
                  {tab !== "my_requests" && <th>Stage</th>}
                  <th>Policy</th>
                  {tab === "my_requests" ? <th>Requested</th> : <th>Requester</th>}
                  <th>{tab === "my_requests" ? "Status" : tab === "decided" ? "My Decision" : "Requested"}</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length > 0 ? rows.map((row) => (
                  <tr key={tab === "my_requests" ? row.id : row.stage_id}>
                    <td>{row.task_title}</td>
                    <td><Badge bg="light" text="dark">{row.priority || "—"}</Badge></td>
                    {tab !== "my_requests" && <td className="small">{row.stage_name}</td>}
                    <td className="small">{row.policy_name}</td>
                    {tab === "my_requests" ? (
                      <td className="small">{row.requested_at ? new Date(row.requested_at).toLocaleString() : "—"}</td>
                    ) : (
                      <td className="small">{row.requested_by_name}</td>
                    )}
                    {tab === "my_requests" ? (
                      <td><Badge bg={requestStatusVariant(row.status)}>{row.status}</Badge></td>
                    ) : tab === "decided" ? (
                      <td className="small"><Badge bg="light" text="dark">{row.decision}</Badge> {row.decision_at ? new Date(row.decision_at).toLocaleDateString() : ""}</td>
                    ) : (
                      <td className="small">{row.requested_at ? new Date(row.requested_at).toLocaleString() : "—"}</td>
                    )}
                    <td className="text-end">
                      <div className="d-flex gap-1 justify-content-end">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="taskq-action-btn"
                          onClick={() => navigate(`/${user?.role}/tasks/${row.task_id}`)}
                          title="Open Task"
                          aria-label={`Open task ${row.task_title || ""}`}
                        >
                          <i className="fa-solid fa-eye" aria-hidden="true"></i>
                          Open Task
                        </Button>
                        {tab === "pending" && (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="taskq-action-btn"
                            onClick={() => openDecide(row)}
                            title="Decide"
                            aria-label={`Decide on ${row.task_title || "task"}`}
                          >
                            <i className="fa-solid fa-gavel" aria-hidden="true"></i>
                            Decide
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="7">
                      <div className="unit-empty">
                        <div className="unit-empty-icon">
                          <i className="fa-solid fa-inbox" aria-hidden="true"></i>
                        </div>
                        <h5>Nothing here</h5>
                        <p>No approval items match this view and the selected filters.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>

        <div className="unit-footer">
          <div className="unit-count-text">Showing <strong>{rows.length}</strong> of <strong>{total}</strong> item(s)</div>
          {totalPages > 1 && (
            <Pagination className="unit-pagination">
              <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)} />
              {compactPageItems([...Array(totalPages)].map((_, i) => (
                <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>{i + 1}</Pagination.Item>
              )), currentPage)}
              <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)} />
            </Pagination>
          )}
        </div>
      </div>

      {decisionTarget && (
        <TaskApprovalDecisionDialog
          show={!!decisionTarget}
          onHide={() => setDecisionTarget(null)}
          requestId={decisionTarget.requestId}
          stage={decisionTarget.stage}
          taskTitle={decisionTarget.taskTitle}
          onDecided={fetchRows}
        />
      )}
    </div>
  );
}
